import * as Styled from "./LiveEventPage.styles";

import { APIError, PageContainer, ToggleButton } from "../../components";
import {
  Alert,
  Button,
  Skeleton,
  Typography,
} from "@mui/material";
import { AttendanceTab, DetailsTab, PhotosTab } from "./Tabs";
import EventFeedbackTab from "./Tabs/EventFeedbackTab/EventFeedbackTab";
import {
  Share as ShareIcon,
} from "@mui/icons-material";
import {
  clearLiveEvent,
  getLiveEvent,
  getLiveEventAttendees,
  getLiveEventPhotos,
  refreshLiveEvent,
  refreshLiveEventAttendees,
  refreshLiveEventPhotos,
} from "../../store/actions/liveEventActions";
import { useEffect, useMemo, useState } from "react";

import { Attendee } from "../../types/attendee";
import { JOIN_EVENT } from "../../services/endpoints";
import {
  CreatedPollModal,
  EventPollModal,
  LiveEventWelcomeModal,
  PollResultsModal,
  ShareEventModal,
} from "../../modals";
import LoadingModal from "../../modals/LoadingModal/LoadingModal";
import { RootState } from "../../store/store";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import axiosAPI from "../../services/axios";
import { useAppDispatch } from "../../store/hooks";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router";
import { useSelector } from "react-redux";
import { getAllPollsForEvent, updatePollStatus } from "../../services/appsync";
import {
  POLL_STATUS,
  transformResponseToPoll,
  Poll as PollObject,
} from '../../modals/CreatedPollModal/PollDataModel'
import { useAppSyncSubscription } from '../../hooks/useAppSyncSubscription'
import EventOptionsModal from '../../modals/EventOptionsModal/EventOptionsModal'
import MatchmakerAttendeeModal from '../../modals/MatchmakerAttendeeModal/MatchmakerAttendeeModal'
import { MATCHMAKER_STATUS, MatchmakerMatch, transformResponseToMatchmaker } from '../../models/matchmakerModel'
import { ListIcon } from "./LiveEventPage.styles";
import theme from "../../theme/theme";

const MINUTES_TO_MS = 60 * 1000;
const REMINDER_MINUTES = 15;
const REMINDER_OFFSET_MS = REMINDER_MINUTES * MINUTES_TO_MS;

const LiveEventPage = () => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { id } = useParams();
  const initialTab = 0;
  const [tab, setTab] = useState(initialTab);
  const [showLiveEventWelcome, setShowLiveEventWelcome] = useState(false);
  const [showCheckingInModal, setShowCheckingInModal] = useState(false);
  const [showEventPollModal, setShowEventPollModal] = useState(false);
  const [showQrCodeDetailsModal, setShowQrCodeDetailsModal] = useState(false);
  const [pushedPollObj, setPushedPollObj] = useState<PollObject | undefined>(
    undefined,
  )
  const [showPollResultsModal, setShowPollResultsModal] = useState(false)
  const [showCreatedPollModal, setShowCreatedPollModal] = useState(false)
  const [pushedPollId, setPushedPollId] = useState<string | null>(null)
  const [showEventOptionsModal, setShowEventOptionsModal] = useState(false)

  const [showMatchmakerAttendeeModal, setShowMatchmakerAttendeeModal] = useState(false)
  const [matchingCompleted, setMatchingCompleted] = useState(false);
  const [matches, setMatches] = useState<MatchmakerMatch[]>([]);

  const user = useSelector((state: RootState) => state.user);
  const attendees = useSelector((state: RootState) => state.liveEvent.attendees);
  const eventLoading = useSelector((state: RootState) => state.liveEvent.loading);
  const eventLoadingError = useSelector((state: RootState) => state.liveEvent.error);
  const event = useSelector((state: RootState) => state.liveEvent.data);

  const isEventOrganizer = useMemo(
    () => user.email === event?.eventOrganizerEmail,
    [user.email, event?.eventOrganizerEmail],
  )

  const currentTime = new Date();
  const isPastEvent = event?.endDateTime
    ? currentTime > new Date(event.endDateTime * 1000)
    : false;

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const generateEventLink = (eventId: string) => {
    const url = new URL(window.location.href)
    const baseLink = `${url.protocol}//${url.host}`
    return `${baseLink}/events?eventId=${eventId}`
  }

  useEffect(() => {
    const url = new URL(window.location.href);
    const tabName = url.searchParams.get("tab");

    if (tabName === "photos") {
      setTab(1);
    } else if (tabName === "attendance") {
      setTab(2);
    } else if (
      tabName === "event-feedback" &&
      isEventOrganizer &&
      isPastEvent
    ) {
      setTab(3);
    } else {
      setTab(0);
    }

    url.searchParams.set("tab", tabName || "details");
    window.history.replaceState(null, "", url.href);
  }, [isEventOrganizer, isPastEvent]);

  const tabOptions = ["Details", "Photos", "Attendance"];
  if (isEventOrganizer && isPastEvent) {
    tabOptions.push("Event Feedback");
  }

  useEffect(() => {
    if (id && id !== "") {
      if (id === event?.eventId) {
        dispatch(refreshLiveEvent(id));
        dispatch(refreshLiveEventPhotos(id));
        dispatch(refreshLiveEventAttendees(id));
      } else {
        dispatch(getLiveEvent(id));
        dispatch(getLiveEventAttendees(id));
        dispatch(getLiveEventPhotos(id));
      }
      const intervalId = setInterval(() => {
        dispatch(refreshLiveEvent(id));
        dispatch(refreshLiveEventPhotos(id));
        dispatch(refreshLiveEventAttendees(id));
      }, 60000);
      return () => {
        clearInterval(intervalId);
        dispatch(clearLiveEvent());
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  useEffect(() => {
    if (!eventLoading && event) {
      const currentTime = new Date();
      const startTime = new Date(event.startDateTime * 1000);
      const fifteenMinutesBeforeStart = new Date(
        startTime.getTime() - 15 * 60 * 1000,
      )
      if (
        user.email !== event.eventOrganizerEmail &&
        (currentTime < fifteenMinutesBeforeStart || event.status !== "ACTIVE")
      ) {
        navigate("/events", {
          state: {
            alert: {
              severity: "error",
              message:
                "The event you tried to join was cancelled or is no longer live.",
            },
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, eventLoading, navigate]);

  useEffect(() => {
    const checkAttendees = async () => {
      const isCheckedIn = attendees.data.find(
        (attendee: Attendee) => attendee.participantEmail === user.email,
      )
      const currentTime = new Date().getTime()
      const eventEndTime = event?.endDateTime
        ? new Date(event.endDateTime).getTime() * 1000
        : null;

      if (
        !isCheckedIn &&
        event &&
        attendees.loading === false &&
        eventEndTime !== null &&
        currentTime <= eventEndTime
      ) {
        setShowCheckingInModal(true);
        const joinEventObj = {
          participantEmail: user.email,
          participantName: user.name,
          participantTitle: user.jobTitle,
        };
        try {
          await axiosAPI.post(JOIN_EVENT(event.eventId), joinEventObj);
          setShowCheckingInModal(false);
          setShowLiveEventWelcome(true);
          dispatch(refreshLiveEventAttendees(event.eventId));
        } catch {
          setShowCheckingInModal(false);
          setShowLiveEventWelcome(false);
        }
      }
    };
    if (!eventLoading && event) {
      checkAttendees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendees.data, event, eventLoading, user.email, dispatch]);

  const renderTabContent = (): JSX.Element | null => {
    switch (tab) {
      case 0:
        return <DetailsTab />;
      case 1:
        return <PhotosTab />;
      case 2:
        return <AttendanceTab />;
      case 3:
        return event ? (
          <EventFeedbackTab
            eventId={event.eventId}
            eventName={event.title}
            eventOrganizerEmail={event.eventOrganizerEmail}
          />
        ) : null;
      default:
        return null;
    }
  };


  useAppSyncSubscription(
    event?.eventId || "",
    isEventOrganizer ?? undefined,
    user.email,
    (onUpdateGatherApiPoll) => {
      // Handle poll updates
      if (onUpdateGatherApiPoll) {
        const transformedPoll = transformResponseToPoll([onUpdateGatherApiPoll])[0]
        console.log('transformedPoll', transformedPoll)
        switch (transformedPoll.status) {
          case POLL_STATUS.PUSHED:
            setPushedPollObj(transformedPoll)
            setShowCreatedPollModal(false) // Make sure this is closed - Should never be open for non-organizer
            setShowPollResultsModal(false) // Close poll results modal when new poll is pushed
            setShowEventPollModal(true)
            break
          case POLL_STATUS.COMPLETED:
            setPushedPollObj(undefined)
            setPushedPollId(transformedPoll.pollId)
            setShowEventPollModal(false)
            setShowPollResultsModal(true)
            break
          case POLL_STATUS.CREATED:
            break
          default:
            console.error('Unknown poll status:', transformedPoll.status)
            break
        }
      }
    },
    (onCreateMatchmakerPair) => {
      // Handle matchmaker updates
      if (onCreateMatchmakerPair) {
        const response = transformResponseToMatchmaker(onCreateMatchmakerPair);

        switch (response.status) {
          case MATCHMAKER_STATUS.CREATED:
            setShowMatchmakerAttendeeModal(true);
            setMatchingCompleted(false);
            break;

          case MATCHMAKER_STATUS.COMPLETED:
            const matches = response.participants.filter((match) => match.email && match.email !== user.email)
            setMatches(matches);
            setMatchingCompleted(true);
            break;
        }
      }
    }
  );

  useEffect(() => {
    const getAllPollsForThisEvent = async (eventId?: string) => {
      try {
        if (!eventId) throw new Error("Event ID is required");

        let responsePayload;
        if (isEventOrganizer) {
          responsePayload = await getAllPollsForEvent(eventId);
        } else {
          responsePayload = await getAllPollsForEvent(
            eventId,
            undefined,
            undefined,
            POLL_STATUS.PUSHED,
          )
        }

        const transformedPolls = transformResponseToPoll(responsePayload);
        const pushedObj = transformedPolls.find(
          (poll: PollObject) => poll.status === POLL_STATUS.PUSHED,
        )

        if (!isEventOrganizer && pushedObj) {
          setPushedPollObj(pushedObj);
          setPushedPollId(pushedObj.pollId);
          setShowEventPollModal(true);
        }
      } catch (error) {
        console.error("Error getting polls: ", error);
      }
    };

    if (event?.eventId) {
      getAllPollsForThisEvent(event.eventId);
    }
  }, [event, isEventOrganizer]);

  const handlePushingPoll = async (pollId: string, eventId?: string) => {
    if (pollId !== null && eventId !== undefined) {
      try {
        await updatePollStatus(eventId, pollId, POLL_STATUS.PUSHED);
        setShowCreatedPollModal(false);
        setShowPollResultsModal(true);
      } catch (error) {
        console.error("Error pushing poll: ", error);
      }
    }
  };

  const handleOpenCreatedPollModal = () => {
    setShowEventOptionsModal(false)
    setTimeout(() => {
      setShowCreatedPollModal(true)
    }, 100) // wait for options modal to close
  }

  return (
    <PageContainer>
      {eventLoadingError ? (
        <APIError />
      ) : (
        <>
          <MatchmakerAttendeeModal
            open={showMatchmakerAttendeeModal}
            onClose={(event: Event, reason: string) => {
              if (reason !== "backdropClick") {
                setShowMatchmakerAttendeeModal(false);
              }
            }}
            eventId={event?.eventId || ""}
            matchingCompleted={matchingCompleted}
            matches={matches}
          />
          <LoadingModal
            open={showCheckingInModal}
            onClose={() => {
              setShowCheckingInModal(false);
            }}
          />
          <LiveEventWelcomeModal
            open={showLiveEventWelcome}
            onClose={() => {
              window.history.replaceState(null, "");
              setShowLiveEventWelcome(false);
            }}
          />
          <Styled.Header>
            {event &&
              new Date() <
              new Date(event.startDateTime * 1000 - REMINDER_OFFSET_MS) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This event is not live yet, but attendees will be able to join
                  at{" "}
                  {new Date(
                    event.startDateTime * 1000 - REMINDER_OFFSET_MS,
                  ).toLocaleString(undefined, {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                  })}
                  .
                </Alert>
              )}
            {event &&
              new Date() >=
              new Date(event.startDateTime * 1000 - -REMINDER_OFFSET_MS) &&
              new Date() < new Date(event.startDateTime * 1000) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This event will commence in{" "}
                  {Math.round(
                    (new Date(event.startDateTime * 1000).getTime() -
                      new Date().getTime()) /
                    (1000 * 60),
                  )}{' '}
                  minutes.
                </Alert>
              )}
            {event && new Date() > new Date(event.endDateTime * 1000) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This event ended{" "}
                {new Date(event.endDateTime * 1000).toLocaleString(undefined, {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                })}
                .
              </Alert>
            )}
            <Styled.HeaderStack
              direction={{
                mobile: "column",
                tablet: "row",
                desktop: "row",
              }}
              spacing={1}
              width="100%"
            >
              {eventLoading || !event ? (
                <Styled.HeaderTextSkeleton variant="rectangular" />
              ) : (
                <Styled.HeaderText variant="h1" gutterBottom>
                  {event.title}
                </Styled.HeaderText>
              )}

              {attendees.loading || !attendees.data ? (
                <Styled.HeaderActionContainer>
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      height: "48px",
                      width: "100%",
                    }}
                  />
                </Styled.HeaderActionContainer>
              ) : (
                <Styled.HeaderActionContainer>

                  {showPollResultsModal && (
                    <PollResultsModal
                      open={showPollResultsModal}
                      onClose={() => setShowPollResultsModal(false)}
                      variant="live-event"
                      passedPoll={
                        pushedPollId && id
                          ? { pollId: pushedPollId, eventId: id }
                          : undefined
                      }
                      organizerOrAdmin={isEventOrganizer}
                      onPushNextPoll={() => {
                        setShowPollResultsModal(false);
                        setTimeout(() => setShowCreatedPollModal(true), 100);
                      }}
                    />
                  )}

                  {showEventPollModal && pushedPollObj && (
                    <EventPollModal
                      open={showEventPollModal}
                      pollObj={pushedPollObj}
                      participantEmail={user.email}
                      onClose={() => {
                        setShowEventPollModal(false);
                        setPushedPollObj(undefined);
                      }}
                    />
                  )}

                  {showCreatedPollModal && (
                    <CreatedPollModal
                      open={showCreatedPollModal}
                      onClose={() => setShowCreatedPollModal(false)}
                      eventId={event?.eventId ? event.eventId : ""}
                      variant={isPastEvent ? "past-event" : "live-event"}
                      pushedPollId={pushedPollId}
                      onPushPoll={(pollId) => {
                        setPushedPollId(pollId);
                        handlePushingPoll(
                          pollId,
                          event?.eventId ? event.eventId : id,
                        )
                      }}
                    />
                  )}

                  {event?.feedbackLink && (
                    <Button
                      variant="text"
                      startIcon={<StarOutlineIcon />}
                      onClick={() => {
                        window.open(event.feedbackLink, "_blank");
                      }}
                    >
                      Give Feedback
                    </Button>
                  )}
                </Styled.HeaderActionContainer>
              )}
              {attendees.loading || !attendees.data ? (
                <Styled.HeaderActionContainer>
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      height: '48px',
                      width: '100%',
                    }}
                  ></Skeleton>
                </Styled.HeaderActionContainer>
              ) : (
                <Styled.HeaderActionContainer>
                  {isEventOrganizer ? (
                    <Button
                      variant="text"
                      startIcon={<ListIcon />}
                      onClick={() => {
                        setShowEventOptionsModal(true)
                      }}
                    >
                      Event Options
                    </Button>
                  ) : (<><Styled.LinkButtonContainer
                    onClick={() => {
                      setShowQrCodeDetailsModal(true);
                    }}
                  >
                    <ShareIcon color="primary" />
                    <Typography
                      variant="body2"
                      style={{
                        marginLeft: theme.spacing(1),
                        marginRight: theme.spacing(1),
                      }}
                    >
                      Share Event
                    </Typography>
                  </Styled.LinkButtonContainer>
                    <ShareEventModal
                      link={generateEventLink(event ? event.eventId : '')}
                      open={showQrCodeDetailsModal}
                      onClose={() => {
                        setShowQrCodeDetailsModal(false)
                      }}
                    />
                  </>)
                  }
                </Styled.HeaderActionContainer>
              )}
              {showEventOptionsModal && isEventOrganizer && (
                <EventOptionsModal
                  open={showEventOptionsModal}
                  onClose={() => setShowEventOptionsModal(false)}
                  eventId={event?.eventId || ''}
                  pushedPollId={pushedPollId}
                  onPushPoll={(pollId) => {
                    setPushedPollId(pollId)
                    handlePushingPoll(
                      pollId,
                      event?.eventId ? event.eventId : id,
                    )
                  }}
                  userEmail={user.email}
                  onPollClick={handleOpenCreatedPollModal}
                />
              )}
            </Styled.HeaderStack>
            <ToggleButton
              option={tab}
              setOption={setTab}
              onClick={(index: number) => {
                setTab(index);
                navigate(`?tab=${tabOptions[index].toLowerCase()}`);
              }}
              options={tabOptions}
            />
          </Styled.Header>
          {renderTabContent()}
        </>
      )}
    </PageContainer>
  );
};

export default LiveEventPage;
